// src/utils/tactic/planSummary.js
// ─────────────────────────────────────────────────────────────────────────
// Turn a playbook into something a language model can actually reason about.
//
// THE PROBLEM WITH SENDING THE RAW DOCUMENT
// Keyframes are coordinates. "Valhein at (0.73, 0.35)" tells a model nothing,
// and asking it to work out who can see whom would be asking it to run a
// raycaster in its head — which is exactly where models invent things.
//
// SO WE DO THE GEOMETRY HERE
// Every position is resolved to a NAMED PLACE on the map, and the vision engine
// that already drives the board answers the sight questions outright: how much
// of the field each side holds, which objectives are lit, which enemies are
// visible. The model receives facts and is asked only to judge them. This is
// the same discipline the stats coach uses (see api/coachFaithfulness.js) —
// numbers come from the app, never from the model.
// ─────────────────────────────────────────────────────────────────────────

import {
  BASES,
  TOWERS,
  buildBlockingSegments,
  buildObjectives,
} from "./mapTerrain";
import { sampleTokenPosition } from "./playbook";
import { SIGHT_RADIUS, computeTeamVision, isPointVisible } from "./visibility";

// Fixed landmarks — the ones that are not part of the editable objective set.
const STATIC_PLACES = [
  ...BASES.map((b) => ({ name: `ป้อมใหญ่ฝั่ง${b.side === "blue" ? "บลู" : "เรด"}`, pos: b.pos })),
  ...TOWERS.map((t) => ({
    name: `ป้อม${t.lane === "top" ? "ออฟเลน" : t.lane === "bot" ? "แครี่" : "มิด"} T${t.tier} ฝั่ง${
      t.side === "blue" ? "บลู" : "เรด"
    }`,
    pos: t.pos,
  })),
  { name: "แม่น้ำฝั่งสเลเยอร์", pos: [0.27, 0.73] },
  { name: "แม่น้ำฝั่งดราก้อน", pos: [0.73, 0.27] },
  { name: "กลางแมพ", pos: [0.5, 0.5] },
];

/**
 * Places a coach would actually name. Objectives come first so a hand-placed
 * camp wins over a nearby generic feature.
 */
function buildPlaces(landmarks) {
  return [
    ...buildObjectives(landmarks).map((o) => ({ name: o.label, pos: o.pos })),
    ...STATIC_PLACES,
  ];
}

/** How far a token can be from a landmark and still count as "at" it. */
const NEAR = 0.09;

const dist = (ax, ay, bx, by) => Math.hypot(ax - bx, ay - by);

/**
 * The named place a point sits at, or a lane/jungle description when it is not
 * near anything in particular.
 */
export function describePlace(x, y, landmarks) {
  let best = null;
  let bestD = Infinity;
  for (const p of buildPlaces(landmarks)) {
    const d = dist(x, y, p.pos[0], p.pos[1]);
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  if (best && bestD <= NEAR) return best.name;

  // Mid lane runs along y = x; the two side lanes hug the outer edges.
  const offMid = y - x;
  const half = x + y < 1 ? "ครึ่งบลู" : "ครึ่งเรด";
  if (Math.abs(offMid) < 0.08) return `มิดเลน (${half})`;
  if (offMid > 0.34) return `ออฟเลน (${half})`;
  if (offMid < -0.34) return `แครี่ (${half})`;
  return `ป่า${offMid > 0 ? "ฝั่งออฟเลน" : "ฝั่งแครี่"} (${half})`;
}

const GRID = 32; // field coverage is sampled on a 32×32 lattice

/**
 * What one side can see at a moment: share of the field, which objectives are
 * lit, and which enemies are caught out.
 */
function readVision({ tokens, t, team, terrain, segments, downedTowers, towerVision, objectives }) {
  const observers = tokens
    .filter((tok) => tok.vision && tok.team === team)
    .map((tok) => {
      const pos = sampleTokenPosition(tok, t);
      return { id: tok.id, x: pos.x, y: pos.y, radius: tok.visionRadius || SIGHT_RADIUS.hero };
    });

  if (towerVision) {
    const down = new Set(downedTowers);
    for (const tower of [...TOWERS, ...BASES]) {
      if (tower.side !== team || down.has(tower.id)) continue;
      observers.push({
        id: tower.id,
        x: tower.pos[0],
        y: tower.pos[1],
        radius: SIGHT_RADIUS.tower,
      });
    }
  }

  const brush = terrain?.brush || [];
  const vision = observers.length
    ? computeTeamVision({ observers, segments, brush })
    : null;

  let seen = 0;
  if (vision) {
    for (let i = 0; i < GRID; i++) {
      for (let j = 0; j < GRID; j++) {
        const x = (i + 0.5) / GRID;
        const y = (j + 0.5) / GRID;
        if (isPointVisible(x, y, vision, brush)) seen += 1;
      }
    }
  }

  const objectivesLit = {};
  for (const o of objectives) {
    objectivesLit[o.label] = vision
      ? isPointVisible(o.pos[0], o.pos[1], vision, brush)
      : false;
  }

  const enemiesVisible = tokens
    .filter((tok) => tok.team && tok.team !== team && tok.kind === "hero")
    .filter((tok) => {
      const pos = sampleTokenPosition(tok, t);
      return vision ? isPointVisible(pos.x, pos.y, vision, brush) : false;
    })
    .map((tok) => tok.heroName || tok.label)
    .filter(Boolean);

  return {
    fieldCoveragePct: Math.round((seen / (GRID * GRID)) * 100),
    objectivesLit,
    enemiesVisible,
  };
}

/** The moments worth reading: the coach's own phase flags, or thirds. */
function momentsFor(playbook) {
  const duration = playbook.duration || 30;
  if (playbook.phases?.length) {
    return playbook.phases
      .filter((p) => p.t <= duration)
      .map((p) => ({ t: p.t, label: p.label || `นาทีที่ ${p.t}` }));
  }
  return [
    { t: 0, label: "เริ่มต้น" },
    { t: duration / 2, label: "กลางคลิป" },
    { t: duration, label: "จบคลิป" },
  ];
}

const round = (n) => Math.round(n * 1000) / 1000;

/**
 * Compact, fully-resolved description of a plan.
 *
 * Everything numeric in here is computed by the app, so the model's job is
 * judgement, not arithmetic or geometry.
 */
export function summarizePlan({ playbook, terrain, towerVision = true, landmarks }) {
  const tokens = (playbook?.tokens || []).filter((tok) => tok.keys?.length);
  const segments = buildBlockingSegments(terrain);
  const downedTowers = playbook?.downedTowers || [];
  const objectives = buildObjectives(landmarks);

  const lineup = { blue: [], red: [] };
  for (const tok of tokens) {
    if (tok.kind !== "hero") continue;
    const side = tok.team === "red" ? "red" : "blue";
    lineup[side].push({ hero: tok.heroName || "?", lane: tok.role || "ไม่ระบุ" });
  }

  const moments = momentsFor(playbook).map((moment) => {
    const positions = tokens.map((tok) => {
      const pos = sampleTokenPosition(tok, moment.t);
      return {
        name: tok.heroName || tok.label || tok.kind,
        kind: tok.kind,
        team: tok.team,
        lane: tok.role || null,
        at: describePlace(pos.x, pos.y, landmarks),
        x: round(pos.x),
        y: round(pos.y),
        givesVision: Boolean(tok.vision),
      };
    });

    const shared = { tokens, t: moment.t, terrain, segments, downedTowers, towerVision, objectives };
    return {
      t: moment.t,
      label: moment.label,
      positions,
      vision: {
        blue: readVision({ ...shared, team: "blue" }),
        red: readVision({ ...shared, team: "red" }),
      },
    };
  });

  const wards = tokens
    .filter((tok) => tok.kind === "ward")
    .map((tok) => {
      const pos = sampleTokenPosition(tok, 0);
      return { team: tok.team, at: describePlace(pos.x, pos.y, landmarks) };
    });

  return {
    meta: {
      name: playbook?.name || "",
      notes: playbook?.notes || "",
      ourSide: playbook?.ourSide || "blue",
      durationSec: playbook?.duration || 30,
      phaseCount: playbook?.phases?.length || 0,
    },
    lineup,
    wards,
    downedTowers: downedTowers.map((id) => {
      const tower = TOWERS.find((t) => t.id === id);
      return tower
        ? `ป้อม${tower.lane} T${tower.tier} ฝั่ง${tower.side === "blue" ? "บลู" : "เรด"}`
        : id;
    }),
    towerVisionEnabled: towerVision,
    // The board no longer draws fog, and there is no terrain to block sight, so
    // these numbers are line-of-sight-to-the-map-edge only. Stated in the
    // payload so the model qualifies any claim it builds on them instead of
    // reporting them as what a player would really see.
    visionModel: "รัศมีสายตาอย่างเดียว ไม่มีกำแพงหรือหญ้าบัง ตัวเลขจึงเป็นขอบบน",
    marks: (playbook?.drawings || []).length,
    moments,
  };
}
